import React, { useEffect, useState } from 'react';

const CONSENT_KEY = 'zodiage_ad_consent_v1';

type ConsentState = 'accepted' | 'declined' | null;

function getStoredConsent(): ConsentState {
  try {
    const val = localStorage.getItem(CONSENT_KEY);
    if (val === 'accepted' || val === 'declined') return val;
  } catch (_) {}
  return null;
}

// Signals Google AdSense to serve non-personalized ads when consent is declined.
// See: https://support.google.com/adsense/answer/9007336
function applyConsentMode(accepted: boolean) {
  try {
    const adsbygoogle = (window as any).adsbygoogle;
    if (Array.isArray(adsbygoogle)) {
      adsbygoogle.push({
        params: {
          google_npa: accepted ? '0' : '1', // '1' = non-personalized
        },
      });
    }
  } catch (_) {}
}

const CookieConsent: React.FC = () => {
  const [consent, setConsent] = useState<ConsentState>(() => getStoredConsent());

  // Apply stored consent on mount so returning visitors get correct ad mode immediately
  useEffect(() => {
    if (consent === 'accepted') applyConsentMode(true);
    if (consent === 'declined') applyConsentMode(false);
  }, []);

  // Banner is hidden if consent already recorded
  if (consent !== null) return null;

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    applyConsentMode(true);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setConsent('declined');
    applyConsentMode(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'rgba(7, 11, 31, 0.97)',
        borderTop: '1px solid #1e2952',
        padding: '16px 24px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
      }}
    >
      <p
        style={{
          flex: '1 1 280px',
          margin: 0,
          fontSize: '13px',
          lineHeight: '1.6',
          color: '#a0a0c0',
        }}
      >
        We use cookies to serve personalised ads via Google AdSense and to remember your preferences.
        You can accept personalised ads or choose non-personalised ads.
        See our{' '}
        <a
          href="/privacy"
          style={{ color: '#c0b8ff', textDecoration: 'underline' }}
        >
          Privacy Policy
        </a>{' '}
        for details.
      </p>

      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleDecline}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid #1e2952',
            background: 'transparent',
            color: '#a0a0c0',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          Non-personalised ads
        </button>
        <button
          onClick={handleAccept}
          style={{
            padding: '8px 20px',
            borderRadius: '6px',
            border: 'none',
            background: 'linear-gradient(to right, #7c3aed, #ec4899)',
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Accept & continue
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
